import PinList from "@/components/PinList";
import { AB_PINS_SUCAI_THEME } from "@/constants/ABMng";
import { AD_POSITION } from "@/constants/ads";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import { getAds, getRecommendPins, getRecommendTopics } from "@/services";
import { getPinDetailModal } from "@/utils/getPinDetailModal";
import useABTest from "@/utils/hooks/useABtest";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import useRequest from "@/utils/useReq";
import { useSetState } from "ahooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
// 为你推荐
function PinRecommendPinList({
  pinId,
  isModal,
  fileId,
  similiarList,
  showSimilarList,
  customColumn,
  customColumnSize,
  relatedBoards, // 新增：同在画板数据
  currentBoard = null, // 新增：当前采集所在画板
  artRecommend = null, // 新增：AI灵感推荐数据（可选）
  searchWord = null, // 新增：搜索词（可选）
}) {
  // 会员判断：会员显示版权标识，非会员不显示
  const isVip = useIsMaterialVip();

  const [query, setQuery] = useSetState({
    page: 1,
    pinId: null,
  });
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [ads, setAds] = useState([]);
  const [scrollableTarget, setScrollableTarget] = useState(null);
  const [masonryKey, setMasonryKey] = useState(null);

  // 主题合集相关状态
  const [topicCollections, setTopicCollections] = useState([]);
  const [topicCurrentPage, setTopicCurrentPage] = useState(0); // 当前已加载的主题页数
  const [topicHasMore, setTopicHasMore] = useState(true); // 是否还有更多主题可以获取

  // 滚动监听状态
  const [hasScrolledOneScreen, setHasScrolledOneScreen] = useState(false);

  // 旧版详情页为你推荐插入素材专题卡片AB测试
  const pinsSucaiThemeAB = useABTest(AB_PINS_SUCAI_THEME);

  // 判断是否显示主题合集
  const isShowTopicCollection = useMemo(() => {
    // 等待AB测试加载完成后再判断
    if (pinsSucaiThemeAB.loading) {
      return false; // 加载中时不显示，避免闪烁
    }
    return pinsSucaiThemeAB.new;
  }, [pinsSucaiThemeAB.loading, pinsSucaiThemeAB.new]);

  // 解决瀑布流容器大小判断，因为弹窗过程需要时间，延迟计算
  useEffect(() => {
    const timeout = setTimeout(() => {
      setMasonryKey(pinId);
    }, 400);

    return () => {
      clearTimeout(timeout);
    };
  }, [pinId]);

  useRequest(
    async () => {
      const { page, pinId } = query;

      if (!pinId) {
        return;
      }

      const [result, waterfallAd] = await Promise.all([
        getRecommendPins({
          pin_id: pinId,
          limit: 20,
          page,
        }),
        page > 1 && page % 2 === 1 // 第一页不展示广告，每40个插一
          ? getAds({
              positions: [AD_POSITION.PINDETAIL_WATERFALL_PER_PAGE],
              variables: {
                user_tag: ["pin"],
              },
            })
          : null,
      ]);

      const pins = result?.pins || [];

      if (waterfallAd?.[0] && pins.length > 0) {
        //追加到列表前
        pins.unshift({
          ...waterfallAd[0],
          pin_id: `recommend-${waterfallAd[0].material.id}-${page}`,
          type: "recommend",
        });
      }

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [query],
    }
  );

  useRequest(async () => {
    const result = await getAds({
      positions: [53, 57],
      file_id: fileId,
    });

    setAds(
      result?.reduce((result, item) => {
        if (item) {
          result.push({
            ...item,
            pin_id: `recommend-${item.material.id}`,
            type: "recommend",
          });
        } else {
          result.push(null);
        }
        return result;
      }, []) || []
    );
  });

  useEffect(() => {
    if (pinId) {
      setHasMore(true);
      setDataSource([]);
      setQuery({
        page: 1,
        pinId,
      });

      // 重置滚动状态和主题合集状态，新的pinId需要重新监听滚动
      setHasScrolledOneScreen(false);
      setTopicCurrentPage(0);
      setTopicHasMore(true);
      setTopicCollections([]);
    }
  }, [pinId, setQuery]);

  useEffect(() => {
    if (isModal) {
      const modal = getPinDetailModal();
      if (modal) {
        setScrollableTarget(modal);
      }
    }
  }, [isModal]);

  // 滚动监听逻辑（用于主题合集）
  useEffect(() => {
    // 已滚动一屏或浏览器环境不可用时，不再监听
    if (hasScrolledOneScreen) return;

    const handleScroll = () => {
      const scrollContainer = isModal ? getPinDetailModal() : window;

      let scrollY, viewportHeight;

      if (isModal && scrollContainer) {
        scrollY = scrollContainer.scrollTop;
        viewportHeight = scrollContainer.clientHeight;
      } else {
        scrollY = window.scrollY;
        viewportHeight = window.innerHeight;
      }

      // 当滚动超过一屏高度时，触发数据加载
      if (scrollY > viewportHeight) {
        setHasScrolledOneScreen(true);
      }
    };

    const scrollTarget = isModal ? getPinDetailModal() : window;

    if (scrollTarget) {
      scrollTarget.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        scrollTarget.removeEventListener("scroll", handleScroll);
      };
    }
  }, [isModal, hasScrolledOneScreen]);

  // 获取主题合集数据
  const fetchTopicCollections = useMemo(() => {
    return async () => {
      // 关键修复：只有在用户滚动一屏后才加载数据，pinId变化时不立即加载
      if (!hasScrolledOneScreen) {
        return;
      }

      // 只有当第一页还没有加载时才获取第一页
      if (topicCurrentPage > 0) {
        return;
      }

      try {
        let topics = [];

        // 如果有pinId，获取第一页专题数据
        if (pinId) {
          try {
            const result = await getRecommendTopics({
              pin_id: pinId,
              q: searchWord || "", // 没有searchWord时传空字符串
              page_num: 1,
              page_size: 1, // 只获取第一个主题
            });

            if (result.topics && result.topics.length > 0) {
              topics = result.topics;
            }
          } catch (apiError) {
            console.warn("获取主题数据失败:", apiError);
          }
        }

        // 转换数据格式
        const processedTopics = topics
          .map((item) => ({
            ...item,
            pin_id: `topic_${item.filter_id}`,
            type: RENDERCARDTYPE.topicCollectionA,
            // 适配不同的字段名
            urlname: item.url_name || item.urlname,
          }))
          // 过滤掉没有采集的主题
          .filter((item) => item?.pins?.length > 0);

        setTopicCollections(processedTopics);
        setTopicCurrentPage(1); // 标记第一页已加载

        // 如果第一页就没有数据，标记为无更多数据
        if (processedTopics.length === 0) {
          setTopicHasMore(false);
        }
      } catch (error) {
        console.error("获取主题合集失败:", error);
        setTopicCollections([]);
      }
    };
  }, [hasScrolledOneScreen, topicCurrentPage, pinId, searchWord]);

  // 只有在AB测试开启时才执行数据获取
  useEffect(() => {
    if (!isShowTopicCollection) {
      return;
    }
    fetchTopicCollections();
  }, [isShowTopicCollection, fetchTopicCollections]);

  // 获取更多主题合集数据（在滚动加载时调用）
  const fetchMoreTopicCollections = useCallback(async () => {
    // 只有在AB测试开启且已经获取过第一页时才继续
    if (!isShowTopicCollection || topicCurrentPage === 0) {
      return;
    }

    // 如果已经没有更多数据，直接返回
    if (!topicHasMore) {
      return;
    }

    try {
      let topics = [];

      // 如果有pinId，获取下一页专题数据
      if (pinId) {
        try {
          const result = await getRecommendTopics({
            pin_id: pinId,
            q: searchWord || "", // 没有searchWord时传空字符串
            page_num: topicCurrentPage + 1,
            page_size: 1, // 只获取一个主题
          });

          if (result.topics && result.topics.length > 0) {
            topics = result.topics;
          }
        } catch (apiError) {
          console.warn("获取更多主题数据失败:", apiError);
          return;
        }
      }

      // 转换数据格式
      const processedTopics = topics
        .map((item) => ({
          ...item,
          pin_id: `topic_${item.filter_id}`,
          type: RENDERCARDTYPE.topicCollectionA,
          // 适配不同的字段名
          urlname: item.url_name || item.urlname,
        }))
        // 过滤掉没有采集的主题
        .filter((item) => item?.pins?.length > 0);

      if (processedTopics.length > 0) {
        // 追加到现有主题列表
        setTopicCollections((prev) => [...prev, ...processedTopics]);
        setTopicCurrentPage(topicCurrentPage + 1);
      } else {
        // 如果获取到空数据，标记为无更多数据
        setTopicHasMore(false);
      }
    } catch (error) {
      console.warn("获取更多主题合集失败:", error);
    }
  }, [
    isShowTopicCollection,
    topicCurrentPage,
    topicHasMore,
    pinId,
    searchWord,
  ]);

  const onLoadMore = useCallback(() => {
    if (dataSource.length > 0) {
      setQuery({
        page: query.page + 1,
      });
    }

    // 在滚动加载时获取更多主题合集
    fetchMoreTopicCollections();
  }, [dataSource, query, setQuery, fetchMoreTopicCollections]);

  const list = useMemo(() => {
    let list = [...dataSource];

    // 1. 先插入相似采集运营位
    if (showSimilarList && similiarList.data?.pins?.length) {
      list.unshift({ ...similiarList, type: RENDERCARDTYPE.similar, pinId });
    }

    // 2. 插入同在画板运营位
    if (
      relatedBoards &&
      relatedBoards.length >= 3 &&
      relatedBoards.length < 10
    ) {
      // 如果有「所在画板」，且不在列表第一位，则添加到第一位
      let boardsToShow = relatedBoards;
      if (
        currentBoard &&
        relatedBoards[0]?.board_id !== currentBoard.board_id
      ) {
        boardsToShow = [currentBoard, ...relatedBoards];
      }

      list.unshift({
        type: RENDERCARDTYPE.boardOperation,
        boards: boardsToShow,
        pinId: pinId,
        currentBoardId: currentBoard?.board_id, // 传递当前采集所在画板的ID，用于显示角标
        pin_id: `boardOperation-${pinId}`, // 添加pin_id避免被过滤
        mergeCol: 3, // 关键：告诉Masonry组件这个元素占用3列
      });
    }

    // 3. 插入AI灵感推荐运营位（确保在第一位）
    if (artRecommend && searchWord) {
      list.unshift(artRecommend);
    }

    // 4. 插入主题合集卡片
    if (topicCollections.length > 0) {
      // 新规则：第一个展示在瀑布流第40位，之后每30个内容展示一个
      // 从后往前插入，避免索引偏移问题
      for (let i = topicCollections.length - 1; i >= 0; i--) {
        const targetPosition = 40 + i * 30; // 第一个在第40位，第二个在第70位，第三个在第100位...

        // 检查是否有足够的列表长度来插入
        if (topicCollections[i] && list.length >= targetPosition) {
          list.splice(targetPosition, 0, topicCollections[i]);
        }
      }
    }

    // 5. 如果没有数据或广告，直接返回
    if (dataSource.length <= 0 || ads.length <= 0) {
      return list;
    }

    // 6. 插入广告运营位
    return [
      ...list.slice(0, 3),
      ...ads.slice(1, 2).filter((i) => i),
      ...list.slice(3),
    ];
  }, [
    dataSource,
    ads,
    similiarList,
    pinId,
    showSimilarList,
    relatedBoards,
    currentBoard,
    artRecommend,
    searchWord,
    topicCollections,
  ]);

  if (isModal && !scrollableTarget) {
    return null;
  }

  return (
    <>
      <PinList
        disableModal={!isModal}
        disableQueue={true}
        disableFile={true}
        dataSource={list}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        scrollableTarget={scrollableTarget}
        masonryKey={masonryKey}
        customColumnNum={customColumn}
        customColumnSize={customColumnSize}
        isModal={isModal}
        searchWord={searchWord}
        showMaterialTag={isVip}
      />
    </>
  );
}
export default memo(PinRecommendPinList);
